import mustache from "mustache";
import { AplicacionRequest } from "../../../../modulos/aplicaciones/request/AplicacionRequest";
import { productoGrpcCatalogService } from "../../../../modulos/catalogos/aplicaciones/services/ProductoGrpcCatalogService";
import { ProductoItem } from "../../../../generated/grpc/catalogos_aplicaciones_pb";
import { IFormField } from "../../../definitions/form/IFormField";
import { AutocompleteRender } from "../../../implementations/form/autocomplete/AutocompleteRender";
import rowTemplate from "./aplicacionProductoRowTemplate.html";
import template from "./aplicacionProductosFieldTemplate.html";

mustache.parse(template);
mustache.parse(rowTemplate);

/** Contador para ids únicos por instancia. */
let AP_PRODUCTOS_ID = 0;

/** Campo de productos aplicados (`IFormField`): autocomplete del catálogo
 *  (filtra por descripción/empresa; sin duplicados) + tabla de filas con dosis
 *  editable, unidad del producto y quitar. Escribe `request.detalles[]`; la
 *  lista puede ir vacía, pero toda fila agregada exige dosis > 0. En edición
 *  precarga las filas desde el request y resuelve las unidades del catálogo. */
export class AplicacionProductosField implements IFormField {
  /** Id único de la instancia (para los ids del DOM). */
  private readonly _fieldId: number;
  private readonly _request: AplicacionRequest;
  /** Autocomplete del catálogo de productos. */
  private readonly _autocomplete: AutocompleteRender<ProductoItem>;
  /** Unidad de medida y empresa por productoId (del catálogo). */
  private readonly _productosInfo = new Map<
    string,
    { unidad: string; empresa: string }
  >();

  /**
   * @param request Request que se está editando.
   * @param _onCambio Avisa que cambió la lista o una dosis. Lo usa el formulario
   *                  para repintar el gráfico de producto por lote, que se
   *                  alimenta justo de estas dosis.
   */
  constructor(request: AplicacionRequest, private readonly _onCambio?: () => void) {
    AP_PRODUCTOS_ID += 1;
    this._fieldId = AP_PRODUCTOS_ID;
    this._request = request;
    this._autocomplete = new AutocompleteRender<ProductoItem>({
      placeholder: "Productos",
      service: productoGrpcCatalogService,
      filter: (producto, texto) =>
        // Excluye los que ya están en la tabla (se reevalúa en cada apertura).
        !this._request.detalles.some(
          (detalle) => detalle.productoId === producto.id,
        ) &&
        (producto.descripcion.toLowerCase().includes(texto) ||
          producto.empresa.toLowerCase().includes(texto)),
      getOption: (producto) => ({
        title: producto.descripcion,
        subtitle: producto.empresa,
        badge: producto.unidadMedida,
      }),
      onPick: (producto) => this._onPick(producto),
    });
  }

  getId(): string {
    return `ap-productos-${this._fieldId}`;
  }

  render(): string {
    return mustache.render(template, {
      id: this._fieldId,
      autocomplete: this._autocomplete.render(),
    });
  }

  connect(): void {
    this._autocomplete.connect();
    this._tabla = document.getElementById(`ap-productos-${this._fieldId}`);
    this._rows = document.getElementById(`ap-productos-rows-${this._fieldId}`);
    this._total = document.getElementById(`ap-productos-total-${this._fieldId}`);
    this._error = document.getElementById(`ap-productos-error-${this._fieldId}`);
    this._dosisTh = document.getElementById(
      `ap-productos-dosis-th-${this._fieldId}`,
    );
    // El formulario pudo fijar la etiqueta antes de que existiera el <th>
    // (rearma por metodo en su propio connect, que corre despues de este).
    if (this._dosisTh) this._dosisTh.textContent = this._etiquetaDosis;

    // Delegados: un listener por evento para todas las filas (sobreviven a los
    // re-render de la tabla).
    this._rows?.addEventListener("input", this._onDosisInput);
    this._rows?.addEventListener("click", this._onRemoveClick);

    // Unidad/empresa del catálogo (edición: las filas precargadas las necesitan).
    void this._loadCatalogoAsync();
    this._refreshRows();
  }

  dispose(): void {
    this._disposed = true;
    this._rows?.removeEventListener("input", this._onDosisInput);
    this._rows?.removeEventListener("click", this._onRemoveClick);
    this._autocomplete.dispose();
  }

  /** Toda fila agregada exige dosis > 0 (la lista vacía es válida). */
  getError(): string | null {
    const invalida = this._request.detalles.some(
      (detalle) => detalle.dosis <= 0,
    );
    return invalida ? "Ingrese la dosis de cada producto" : null;
  }

  showError(): void {
    if (this._error) this._error.innerHTML = this.getError() ?? "";
  }

  focus(): void {
    // Con dosis inválida: foco al primer input de dosis en falta; si no, al
    // autocomplete.
    const index = this._request.detalles.findIndex(
      (detalle) => detalle.dosis <= 0,
    );
    if (index >= 0) {
      const input = this._rows?.querySelector<HTMLInputElement>(
        `[data-dosis="${index}"]`,
      );
      if (input) {
        input.focus();
        return;
      }
    }
    this._autocomplete.focus();
  }

  disabled(): void {
    this._autocomplete.disabled();
    this._setRowsDisabled(true);
  }

  enabled(): void {
    this._autocomplete.enabled();
    this._setRowsDisabled(false);
  }

  /** Encabezado de la columna de dosis y su texto actual. La unidad depende del
   *  metodo de la aplicacion -por hectarea en dron, por recipiente en los
   *  manuales-, y el metodo lo elige el formulario, no este campo. */
  private _dosisTh: HTMLElement | null = null;
  private _etiquetaDosis = "Dosis x Ha";

  private _tabla: HTMLElement | null = null;
  private _rows: HTMLElement | null = null;
  private _total: HTMLElement | null = null;
  private _error: HTMLElement | null = null;
  private _disposed = false;

  /** Cambia el encabezado de la columna de dosis. Lo llama el formulario al
   *  cambiar de metodo. Se guarda ademas en un campo porque puede llegar antes
   *  del `connect()`. */
  setEtiquetaDosis(etiqueta: string): void {
    this._etiquetaDosis = etiqueta;
    if (this._dosisTh) this._dosisTh.textContent = etiqueta;
  }

  /** Agrega el producto elegido como fila (ignora duplicados). */
  private _onPick(producto: ProductoItem): void {
    const yaAgregado = this._request.detalles.some(
      (detalle) => detalle.productoId === producto.id,
    );
    if (yaAgregado) return;

    this._productosInfo.set(producto.id, {
      unidad: producto.unidadMedida,
      empresa: producto.empresa,
    });
    this._request.detalles.push({
      id: null,
      productoId: producto.id,
      productoDescripcion: producto.descripcion,
      dosis: 0,
    });
    this._refreshRows();
    this.showError();
    this._onCambio?.();
  }

  /** Carga unidad/empresa del catálogo (para filas precargadas en edición). */
  private async _loadCatalogoAsync(): Promise<void> {
    if (this._request.detalles.length === 0) return;
    const productos = await productoGrpcCatalogService.getListAsync();
    if (this._disposed) return;
    productos.forEach((producto) => {
      this._productosInfo.set(producto.id, {
        unidad: producto.unidadMedida,
        empresa: producto.empresa,
      });
    });
    this._refreshRows();
  }

  /** Re-pinta la tabla desde `request.detalles` (fuente de verdad). */
  private _refreshRows(): void {
    if (!this._tabla || !this._rows || !this._total) return;

    const detalles = this._request.detalles;
    this._tabla.style.display = detalles.length > 0 ? "" : "none";
    this._total.textContent = String(detalles.length);
    this._rows.innerHTML = detalles
      .map((detalle, index) => {
        const info = this._productosInfo.get(detalle.productoId);
        return mustache.render(rowTemplate, {
          index: index,
          nombre: detalle.productoDescripcion,
          empresa: info?.empresa ?? "",
          dosis: detalle.dosis > 0 ? detalle.dosis : "",
          unidad: info?.unidad ?? "",
        });
      })
      .join("");
  }

  /** Delegado: escribe la dosis editada en su detalle. */
  private _onDosisInput = (e: Event): void => {
    const input = e.target as HTMLInputElement | null;
    const index = Number(input?.dataset.dosis ?? "");
    if (!input || isNaN(index) || !this._request.detalles[index]) return;
    const dosis = parseFloat(input.value);
    this._request.detalles[index].dosis = isNaN(dosis) ? 0 : dosis;
    this.showError();
    this._onCambio?.();
  };

  /** Delegado: quita la fila del detalle. */
  private _onRemoveClick = (e: Event): void => {
    const btn = (e.target as HTMLElement | null)?.closest<HTMLElement>(
      "[data-remove]",
    );
    if (!btn) return;
    const index = Number(btn.dataset.remove);
    if (isNaN(index)) return;
    this._request.detalles.splice(index, 1);
    this._refreshRows();
    this.showError();
    this._onCambio?.();
  };

  /** Habilita/deshabilita los controles de las filas. */
  private _setRowsDisabled(disabled: boolean): void {
    this._rows
      ?.querySelectorAll<HTMLInputElement | HTMLButtonElement>("input, button")
      .forEach((el) => {
        el.disabled = disabled;
      });
  }
}
